import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import back from "../../../assets/right-chevron.svg";
import {
  formatDefaultOptionLabel,
  formatDeletePollConfirmMessage,
  getDefaultBooleanOptions,
  getDefaultMcqOptions,
  getPollingQuestionTypes,
  getPollingStatusLabel,
  getPollingStatusOptions,
  PollingScreenStringLabels,
  resolvePollingMessage,
} from "../../../util/Strings";
import {
  clearPollingMessages,
  clearPollResults,
  deletePoll,
  fetchPollForVisit,
  fetchPollResults,
  persistPoll,
  resetPollingVisitState,
} from "../../../slices/PollingSlice";
import ConfirmationDialog from "../../common-components/ConfirmationDialog";
import Toast from "../../common-components/Toast";
import { usePollResultsSocket } from "../../../hooks/usePollResultsSocket";
import "../../../styles/screens/polling/VisitPollingScreen.css";

const POLL_STATUS_OPTIONS = getPollingStatusOptions();
const QUESTION_TYPES = getPollingQuestionTypes();

const createQuestion = (type = "single_choice", prompt = "") => {
  const base = {
    id: `q_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    type,
    prompt,
    required: true,
  };
  if (type === "boolean") {
    return { ...base, options: getDefaultBooleanOptions() };
  }
  return { ...base, options: getDefaultMcqOptions() };
};

function emptyDraftPoll() {
  return {
    name: "",
    status: "draft",
    showPollResults: false,
    questions: [createQuestion("single_choice")],
  };
}

function mapQuestionsFromServer(questions) {
  return (questions || []).map((question) => ({
    id: question.id,
    type: question.type,
    prompt: question.prompt,
    required: question.required,
    options:
      question.type === "boolean"
        ? getDefaultBooleanOptions()
        : [...(question.options || [])],
  }));
}

const RESULT_CHART_COLORS = ["#D04A02", "#FFB600", "#2D2D2D", "#696969", "#464646"];

function getLiveSocketHint(pollStatus, socketStatus) {
  if (pollStatus !== "live") {
    return null;
  }
  if (socketStatus === "connected") {
    return PollingScreenStringLabels.resultsLiveConnected;
  }
  if (socketStatus === "connecting") {
    return PollingScreenStringLabels.resultsLiveConnecting;
  }
  if (socketStatus === "error" || socketStatus === "disconnected") {
    return PollingScreenStringLabels.resultsLiveDisconnected;
  }
  if (socketStatus === "unavailable") {
    return PollingScreenStringLabels.resultsLiveAuthError;
  }
  return null;
}

function mapPollFromServer(data) {
  if (!data) {
    return emptyDraftPoll();
  }
  return {
    _id: data._id,
    name: data.name,
    status: data.status || "draft",
    showPollResults: Boolean(data.showPollResults),
    questions: mapQuestionsFromServer(data.questions),
    updatedAt: data.updatedAt,
  };
}

function PollResultsBlock({
  pollStatus,
  summary,
  loading,
  error,
  socketStatus,
}) {
  if (pollStatus === "draft") {
    return (
      <p className="polling-muted polling-results-empty">
        {PollingScreenStringLabels.resultsDraftHint}
      </p>
    );
  }

  if (loading) {
    return (
      <p className="polling-muted polling-results-empty">
        {PollingScreenStringLabels.resultsLoading}
      </p>
    );
  }

  if (error) {
    return (
      <p className="polling-muted polling-results-empty polling-results-error">
        {PollingScreenStringLabels.resultsUnavailable}
      </p>
    );
  }

  const liveSocketHint = getLiveSocketHint(pollStatus, socketStatus);

  if (!summary || !summary.byQuestion || !summary.byQuestion.length) {
    if (pollStatus === "live") {
      return (
        <div className="polling-results-body polling-results-body--empty">
          <p className="polling-muted polling-results-empty">
            {PollingScreenStringLabels.resultsLiveWaiting}
          </p>
          {liveSocketHint ? (
            <p
              className={`polling-results-socket-hint polling-results-socket-hint--${socketStatus}`}
            >
              {liveSocketHint}
            </p>
          ) : null}
        </div>
      );
    }
    return (
      <p className="polling-muted polling-results-empty">
        {PollingScreenStringLabels.resultsNoResponses}
      </p>
    );
  }

  const statusHint =
    pollStatus === "live"
      ? PollingScreenStringLabels.resultsLiveHint
      : PollingScreenStringLabels.resultsClosedHint;

  return (
    <div className="polling-results-body">
      <p className="polling-results-status-hint">{statusHint}</p>
      {liveSocketHint ? (
        <p
          className={`polling-results-socket-hint polling-results-socket-hint--${socketStatus}`}
        >
          {liveSocketHint}
        </p>
      ) : null}
      <div className="polling-results-total">
        {PollingScreenStringLabels.totalResponsesLabel}{" "}
        <strong>{summary.totalRespondents ?? PollingScreenStringLabels.resultsEmptyValue}</strong>
      </div>
      {summary.byQuestion.map((row) => (
        <div className="polling-results-question" key={row.questionId || row.prompt}>
          <div className="polling-results-q-title">
            {row.prompt || PollingScreenStringLabels.defaultQuestionFallback}
          </div>
          <ul className="polling-results-option-list">
            {(row.options || []).map((opt, optionIndex) => (
              <li key={opt.label} className="polling-results-option-row">
                <div className="polling-results-option-label">
                  <span className="polling-results-option-label-row">
                    <span
                      className="polling-results-swatch"
                      style={{
                        backgroundColor:
                          RESULT_CHART_COLORS[optionIndex % RESULT_CHART_COLORS.length],
                      }}
                    />
                    <span>{opt.label}</span>
                  </span>
                  <span className="polling-results-count">
                    {opt.count} ({opt.pct != null ? `${opt.pct}%` : PollingScreenStringLabels.resultsEmptyValue})
                  </span>
                </div>
                <div className="poll-result-bar-track">
                  <div
                    className="poll-result-bar-fill"
                    style={{
                      width: `${Math.min(100, opt.pct || 0)}%`,
                      backgroundColor:
                        RESULT_CHART_COLORS[optionIndex % RESULT_CHART_COLORS.length],
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function VisitPollingScreen() {
  const { id: visitId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedVisitId = useSelector((state) => state.visits.selectedVisitId);
  const selectedVisitName = useSelector((state) => state.visits.selectedVisitName);
  const fetchStatus = useSelector((state) => state.polling.fetchStatus);
  const saveStatus = useSelector((state) => state.polling.saveStatus);
  const deleteStatus = useSelector((state) => state.polling.deleteStatus);
  const pollingError = useSelector((state) => state.polling.error);
  const lastMessage = useSelector((state) => state.polling.lastMessage);
  const pollsForVisit = useSelector((state) => state.polling.pollsForVisit);
  const loadedVisitId = useSelector((state) => state.polling.loadedVisitId);
  const resultsStatus = useSelector((state) => state.polling.resultsStatus);
  const resultsSummary = useSelector((state) => state.polling.resultsSummary);
  const resultsError = useSelector((state) => state.polling.resultsError);
  const resultsSocketStatus = useSelector((state) => state.polling.resultsSocketStatus);

  const visitHeading =
    visitId && selectedVisitId === visitId && selectedVisitName
      ? selectedVisitName
      : visitId || "";

  const [mode, setMode] = useState("edit");
  const [activeKey, setActiveKey] = useState(null);
  const [poll, setPoll] = useState(() => emptyDraftPoll());
  const [lastSavedAt, setLastSavedAt] = useState("");
  const [pollBaselineStatus, setPollBaselineStatus] = useState("draft");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  usePollResultsSocket({
    visitId,
    pollId: poll._id,
    pollStatus: pollBaselineStatus,
  });

  const dismissToast = useCallback(() => {
    setToast(null);
    dispatch(clearPollingMessages());
  }, [dispatch]);

  useEffect(() => {
    if (lastMessage) {
      setToast({ message: resolvePollingMessage(lastMessage), type: "success" });
      return;
    }
    if (pollingError) {
      setToast({ message: resolvePollingMessage(String(pollingError)), type: "error" });
      return;
    }
    setToast(null);
  }, [lastMessage, pollingError]);

  useEffect(() => {
    setActiveKey(null);
    setPoll(emptyDraftPoll());
    setLastSavedAt("");
    setPollBaselineStatus("draft");
    setMode("edit");
    dispatch(resetPollingVisitState());
  }, [visitId, dispatch]);

  useEffect(() => {
    if (!poll._id || poll.status === "draft") {
      dispatch(clearPollResults());
    }
  }, [poll._id, poll.status, dispatch]);

  useEffect(() => {
    if (!visitId) {
      return undefined;
    }
    let cancelled = false;
    dispatch(fetchPollForVisit(visitId))
      .unwrap()
      .catch(() => {
        if (!cancelled) {
          setMode("edit");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [visitId, dispatch]);

  useEffect(() => {
    if (!visitId || fetchStatus !== "failed") {
      return;
    }
    if (activeKey !== null) {
      return;
    }
    setActiveKey("__new__");
    setPoll(emptyDraftPoll());
    setMode("edit");
  }, [visitId, fetchStatus, activeKey]);

  useEffect(() => {
    if (!visitId || fetchStatus !== "succeeded" || loadedVisitId !== visitId) {
      return;
    }
    if (activeKey !== null) {
      return;
    }
    if (pollsForVisit.length > 0) {
      const first = pollsForVisit[0];
      setActiveKey(first._id);
      setPoll(mapPollFromServer(first));
      setLastSavedAt(first.updatedAt || "");
      setPollBaselineStatus(first.status || "draft");
      setMode("view");
      if (first.status === "closed" && visitId) {
        dispatch(fetchPollResults({ visitId, pollId: first._id }));
      }
    } else {
      setActiveKey("__new__");
      setPoll(emptyDraftPoll());
      setLastSavedAt("");
      setPollBaselineStatus("draft");
      setMode("edit");
    }
  }, [visitId, fetchStatus, loadedVisitId, activeKey, pollsForVisit, dispatch]);

  const canSave = useMemo(() => {
    if (!poll.name.trim()) {
      return false;
    }
    if (!poll.questions.length) {
      return false;
    }
    return poll.questions.every((question) => {
      if (!question.prompt.trim()) {
        return false;
      }
      if (question.type === "boolean") {
        return true;
      }
      const validOptions = question.options.filter((option) => option.trim());
      return validOptions.length >= 2;
    });
  }, [poll]);

  const updatePollField = (field, value) => {
    setPoll((prev) => {
      if (field === "status" && !prev._id) {
        return prev;
      }
      return { ...prev, [field]: value };
    });
  };

  const updateQuestion = (questionId, updates) => {
    setPoll((prev) => ({
      ...prev,
      questions: prev.questions.map((question) =>
        question.id === questionId ? { ...question, ...updates } : question
      ),
    }));
  };

  const addQuestion = (type) => {
    setPoll((prev) => ({
      ...prev,
      questions: [...prev.questions, createQuestion(type)],
    }));
  };

  const removeQuestion = (questionId) => {
    setPoll((prev) => ({
      ...prev,
      questions: prev.questions.filter((question) => question.id !== questionId),
    }));
  };

  const addOption = (questionId) => {
    const question = poll.questions.find((item) => item.id === questionId);
    if (!question || question.type === "boolean") {
      return;
    }
    updateQuestion(questionId, {
      options: [...question.options, formatDefaultOptionLabel(question.options.length + 1)],
    });
  };

  const updateOption = (questionId, optionIndex, value) => {
    const question = poll.questions.find((item) => item.id === questionId);
    if (!question) {
      return;
    }
    const nextOptions = question.options.map((option, index) =>
      index === optionIndex ? value : option
    );
    updateQuestion(questionId, { options: nextOptions });
  };

  const removeOption = (questionId, optionIndex) => {
    const question = poll.questions.find((item) => item.id === questionId);
    if (!question || question.options.length <= 2 || question.type === "boolean") {
      return;
    }
    const nextOptions = question.options.filter((_, index) => index !== optionIndex);
    updateQuestion(questionId, { options: nextOptions });
  };

  const selectPollRow = (row) => {
    dispatch(clearPollingMessages());
    dispatch(clearPollResults());
    setActiveKey(row._id);
    setPoll(mapPollFromServer(row));
    setLastSavedAt(row.updatedAt || "");
    setPollBaselineStatus(row.status || "draft");
    setMode("view");
    if (row.status === "closed" && visitId) {
      dispatch(fetchPollResults({ visitId, pollId: row._id }));
    }
  };

  const startNewPoll = () => {
    dispatch(clearPollingMessages());
    dispatch(clearPollResults());
    setActiveKey("__new__");
    setPoll(emptyDraftPoll());
    setLastSavedAt("");
    setPollBaselineStatus("draft");
    setMode("edit");
  };

  const focusPollAfterDelete = (visitPolls) => {
    dispatch(clearPollResults());
    const remaining = Array.isArray(visitPolls) ? visitPolls : [];
    if (remaining.length > 0) {
      const next = remaining[0];
      setActiveKey(next._id);
      setPoll(mapPollFromServer(next));
      setLastSavedAt(next.updatedAt || "");
      setPollBaselineStatus(next.status || "draft");
      setMode("view");
      if (next.status === "closed" && visitId) {
        dispatch(fetchPollResults({ visitId, pollId: next._id }));
      }
      return;
    }
    startNewPoll();
  };

  const openDeleteConfirm = () => {
    if (pollBaselineStatus === "live") {
      dispatch(clearPollingMessages());
      setToast({
        message: PollingScreenStringLabels.deletePollLiveBlocked,
        type: "error",
      });
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleSavePoll = async () => {
    if (!visitId || !canSave || mode === "view" || !isDraftEditable) {
      return;
    }
    dispatch(clearPollingMessages());
    try {
      const isCreate = !poll._id;
      const result = await dispatch(
        persistPoll({
          visitId,
          pollId: poll._id || null,
          name: poll.name,
          questions: poll.questions,
          status: isCreate ? "draft" : poll.status || "draft",
          showPollResults: Boolean(poll.showPollResults),
          previousStatus: pollBaselineStatus,
        })
      ).unwrap();
      const mapped = mapPollFromServer(result.poll);
      setPoll(mapped);
      setActiveKey(result.poll._id);
      setLastSavedAt(result.poll.updatedAt || "");
      setPollBaselineStatus(mapped.status || "draft");
      setMode("view");
      if (mapped.status === "closed" && visitId) {
        dispatch(fetchPollResults({ visitId, pollId: mapped._id }));
      }
    } catch {
      // error surfaced from Redux
    }
  };

  const handleClosePoll = async () => {
    if (!visitId || !poll._id || pollBaselineStatus !== "live" || isSaving) {
      return;
    }
    dispatch(clearPollingMessages());
    try {
      const result = await dispatch(
        persistPoll({
          visitId,
          pollId: poll._id,
          name: poll.name,
          questions: poll.questions,
          status: "closed",
          showPollResults: Boolean(poll.showPollResults),
          previousStatus: pollBaselineStatus,
        })
      ).unwrap();
      const mapped = mapPollFromServer(result.poll);
      setPoll(mapped);
      setLastSavedAt(result.poll.updatedAt || "");
      setPollBaselineStatus("closed");
      setMode("view");
      dispatch(fetchPollResults({ visitId, pollId: mapped._id }));
    } catch {
      // error surfaced from Redux
    }
  };

  const handleDeletePoll = async () => {
    if (!visitId || !poll._id || pollBaselineStatus === "live" || isDeleting) {
      setShowDeleteConfirm(false);
      return;
    }
    dispatch(clearPollingMessages());
    dispatch(clearPollResults());
    try {
      const result = await dispatch(
        deletePoll({
          visitId,
          pollId: poll._id,
          previousStatus: pollBaselineStatus,
        })
      ).unwrap();
      setShowDeleteConfirm(false);
      focusPollAfterDelete(result.visitPolls);
    } catch {
      setShowDeleteConfirm(false);
    }
  };

  const isFetching = fetchStatus === "loading";
  const isSaving = saveStatus === "loading";
  const isDeleting = deleteStatus === "loading";
  const isBusy = isFetching || isSaving || isDeleting;
  const isNewDraft = activeKey === "__new__" || !poll._id;
  const isDraftEditable = isNewDraft || pollBaselineStatus === "draft";
  const canEditDraft = isDraftEditable && !isBusy;
  const canCloseLive =
    pollBaselineStatus === "live" && poll._id && !isBusy;
  const canDeletePoll =
    poll._id && !isNewDraft && pollBaselineStatus !== "live" && !isBusy;
  const statusOptions = useMemo(() => {
    if (pollBaselineStatus !== "draft") {
      return POLL_STATUS_OPTIONS.filter(
        (option) => option.value === pollBaselineStatus
      );
    }
    if (isNewDraft) {
      return POLL_STATUS_OPTIONS.filter((option) => option.value === "draft");
    }
    return POLL_STATUS_OPTIONS;
  }, [pollBaselineStatus, isNewDraft]);
  const saveDisabled =
    !canSave || isBusy || !visitId || !isDraftEditable;

  const saveDisabledReason = useMemo(() => {
    if (isDeleting) {
      return PollingScreenStringLabels.deletingPoll;
    }
    if (isFetching || isSaving) {
      return PollingScreenStringLabels.saveDisabledLoading;
    }
    if (!canSave) {
      return PollingScreenStringLabels.saveDisabledValidation;
    }
    return null;
  }, [isDeleting, isFetching, isSaving, canSave]);

  const deletePollButton = canDeletePoll ? (
    <button
      type="button"
      className="polling-secondary-btn polling-btn-danger"
      onClick={openDeleteConfirm}
      disabled={isDeleting}
    >
      {isDeleting
        ? PollingScreenStringLabels.deletingPoll
        : PollingScreenStringLabels.deletePoll}
    </button>
  ) : null;

  return (
    <div className="polling-standalone-root">
      {toast ? (
        <Toast
          key={`${toast.type}-${toast.message}`}
          message={toast.message}
          type={toast.type}
          duration={3000}
          onClose={dismissToast}
        />
      ) : null}

      <div className="polling-standalone-nav">
        <button
          type="button"
          className="polling-back-row"
          onClick={() => navigate("/officehome", { replace: true })}
        >
          <img src={back} alt="" className="nav-back-icon" />
          <span>{PollingScreenStringLabels.backToOfficeVisits}</span>
        </button>
        <div className="polling-standalone-nav-right">
          {visitHeading ? (
            <span className="polling-visit-heading">{visitHeading}</span>
          ) : null}
          {visitId ? (
            <button
              type="button"
              className="polling-secondary-btn polling-workspace-btn"
              onClick={() =>
                navigate(`/client/${visitId}/manage-modules/welcome`)
              }
            >
              {PollingScreenStringLabels.openVisitWorkspace}
            </button>
          ) : null}
        </div>
      </div>

      <div className="polling-page">
        <div className="polling-page-header">
          <div className="polling-page-header-main">
            <div className="polling-page-header-text">
              <div className="polling-page-title-row">
                <h2 className="polling-page-title">
                  {PollingScreenStringLabels.standaloneTitle}
                </h2>
                {poll._id ? (
                  <span
                    className={`polling-status-chip polling-status-chip--${poll.status || "draft"}`}
                  >
                    {getPollingStatusLabel(poll.status || "draft")}
                  </span>
                ) : (
                  <span className="polling-toolbar-draft-label">
                    {PollingScreenStringLabels.unsavedDraft}
                  </span>
                )}
              </div>
              <p className="polling-page-description">
                {PollingScreenStringLabels.pageDescription}
              </p>
            </div>

            <div className="polling-page-header-actions">
              {mode === "view" ? (
                <>
                  {canEditDraft ? (
                    <button
                      type="button"
                      className="polling-primary-btn"
                      onClick={() => {
                        dispatch(clearPollingMessages());
                        setMode("edit");
                      }}
                      disabled={isBusy}
                    >
                      {PollingScreenStringLabels.editAgain}
                    </button>
                  ) : null}
                  {canCloseLive ? (
                    <button
                      type="button"
                      className="polling-primary-btn"
                      onClick={handleClosePoll}
                      disabled={isSaving || isDeleting}
                    >
                      {isSaving
                        ? PollingScreenStringLabels.closingPoll
                        : PollingScreenStringLabels.closePoll}
                    </button>
                  ) : null}
                  {deletePollButton}
                  <button
                    type="button"
                    className="polling-secondary-btn"
                    onClick={startNewPoll}
                    disabled={isBusy}
                  >
                    {PollingScreenStringLabels.newPoll}
                  </button>
                </>
              ) : (
                <>
                  {deletePollButton}
                  {!isNewDraft ? (
                    <button
                      type="button"
                      className="polling-secondary-btn"
                      onClick={() => {
                        dispatch(clearPollingMessages());
                        const saved = pollsForVisit.find(
                          (row) => row._id === poll._id
                        );
                        if (saved) {
                          setPoll(mapPollFromServer(saved));
                          setPollBaselineStatus(saved.status || "draft");
                        }
                        setMode("view");
                      }}
                      disabled={isBusy}
                    >
                      {PollingScreenStringLabels.cancelEditing}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="polling-primary-btn"
                    disabled={saveDisabled}
                    onClick={handleSavePoll}
                  >
                    {isSaving
                      ? PollingScreenStringLabels.saving
                      : poll._id
                        ? PollingScreenStringLabels.updatePoll
                        : PollingScreenStringLabels.savePoll}
                  </button>
                </>
              )}
            </div>
          </div>

          {mode === "edit" && saveDisabled && saveDisabledReason ? (
            <div className="polling-page-header-meta">
              <span className="polling-save-hint">{saveDisabledReason}</span>
            </div>
          ) : null}
        </div>

        <div className="polling-workspace-grid">
          <aside className="polling-panel polling-poll-list">
            <h3>{PollingScreenStringLabels.pollsForVisit}</h3>
            <p className="polling-muted polling-poll-list-hint">
              {pollsForVisit.length} {PollingScreenStringLabels.pollsSavedSuffix}
            </p>
            <button
              type="button"
              className="polling-secondary-btn polling-poll-list-new"
              onClick={startNewPoll}
              disabled={isBusy}
            >
              + {PollingScreenStringLabels.newPoll}
            </button>
            {isFetching ? (
              <p className="polling-muted">{PollingScreenStringLabels.loadingPoll}</p>
            ) : null}
            {pollsForVisit.length === 0 && !isFetching ? (
              <p className="polling-muted">{PollingScreenStringLabels.noPollsYet}</p>
            ) : null}
            <ul className="polling-poll-list-items">
              {pollsForVisit.map((row) => (
                <li key={row._id}>
                  <button
                    type="button"
                    className={`polling-poll-list-row${
                      activeKey === row._id ? " polling-poll-list-row--active" : ""
                    }`}
                    onClick={() => {
                      if (isBusy) {
                        return;
                      }
                      selectPollRow(row);
                    }}
                    disabled={isBusy}
                  >
                    <span className="polling-poll-list-name">
                      {row.name || PollingScreenStringLabels.untitled}
                    </span>
                    <span className={`polling-status-chip polling-status-chip--${row.status || "draft"}`}>
                      {getPollingStatusLabel(row.status || "draft")}
                    </span>
                    <span className="polling-poll-list-meta">
                      {row.updatedAt
                        ? new Date(row.updatedAt).toLocaleDateString()
                        : ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <div className="polling-panel polling-editor-panel">
            <h3>{PollingScreenStringLabels.pollMetadataTitle}</h3>
            <label className="polling-field-label">{PollingScreenStringLabels.pollNameLabel}</label>
            <input
              className="polling-input"
              value={poll.name}
              disabled={mode === "view" || isFetching}
              placeholder={PollingScreenStringLabels.pollNamePlaceholder}
              onChange={(event) => updatePollField("name", event.target.value)}
            />

            <label className="polling-field-label">{PollingScreenStringLabels.statusLabel}</label>
            {mode === "edit" && isDraftEditable ? (
              isNewDraft ? (
                <>
                  <div className="polling-status-readonly">
                    <span className="polling-status-chip polling-status-chip--draft">
                      {getPollingStatusLabel("draft")}
                    </span>
                  </div>
                  <p className="polling-muted polling-single-live-note">
                    {PollingScreenStringLabels.statusDraftUntilSavedNote}
                  </p>
                </>
              ) : (
                <>
                  <select
                    className="polling-select"
                    value={poll.status || "draft"}
                    disabled={isFetching || isDeleting}
                    onChange={(event) =>
                      updatePollField("status", event.target.value)
                    }
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="polling-muted polling-single-live-note">
                    {PollingScreenStringLabels.singleLivePolicyNote}
                  </p>
                </>
              )
            ) : (
              <>
                <div className="polling-status-readonly">
                  <span
                    className={`polling-status-chip polling-status-chip--${poll.status || "draft"}`}
                  >
                    {getPollingStatusLabel(poll.status || "draft")}
                  </span>
                </div>
                {!isDraftEditable ? (
                  <p className="polling-muted polling-single-live-note">
                    {pollBaselineStatus === "live"
                      ? PollingScreenStringLabels.deletePollLiveHint
                      : PollingScreenStringLabels.viewOnlyLiveClosedNote}
                  </p>
                ) : null}
              </>
            )}

            <div className="polling-field">
              <span className="polling-field-label">
                {PollingScreenStringLabels.showPollResultsLabel}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(poll.showPollResults)}
                className={`polling-toggle${poll.showPollResults ? " polling-toggle--on" : ""}`}
                disabled={mode === "view" || isFetching || isDeleting || !isDraftEditable}
                onClick={() =>
                  updatePollField("showPollResults", !poll.showPollResults)
                }
              >
                <span className="polling-toggle-track" aria-hidden="true">
                  <span className="polling-toggle-thumb" />
                </span>
                <span className="polling-toggle-state">
                  {poll.showPollResults
                    ? PollingScreenStringLabels.toggleOn
                    : PollingScreenStringLabels.toggleOff}
                </span>
              </button>
              <p className="polling-field-hint">
                {PollingScreenStringLabels.showPollResultsHint}
              </p>
            </div>

            <h3>{PollingScreenStringLabels.questionsTitle}</h3>
            {poll.questions.map((question, questionIndex) => (
              <div className="question-card" key={question.id}>
                <div className="question-card-header">
                  <span className="question-index-chip">
                    {PollingScreenStringLabels.questionIndexPrefix}
                    {questionIndex + 1}
                  </span>
                  {mode === "edit" ? (
                    <button
                      type="button"
                      className="polling-link-btn"
                      onClick={() => removeQuestion(question.id)}
                      disabled={poll.questions.length === 1 || isFetching}
                    >
                      {PollingScreenStringLabels.remove}
                    </button>
                  ) : null}
                </div>

                <label>{PollingScreenStringLabels.questionTypeLabel}</label>
                <select
                  className="polling-select"
                  value={question.type}
                  disabled={mode === "view" || isFetching}
                  onChange={(event) => {
                    const nextType = event.target.value;
                    updateQuestion(question.id, {
                      type: nextType,
                      options:
                        nextType === "boolean"
                          ? getDefaultBooleanOptions()
                          : getDefaultMcqOptions(),
                    });
                  }}
                >
                  {QUESTION_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>

                <label>{PollingScreenStringLabels.promptLabel}</label>
                <input
                  className="polling-input"
                  value={question.prompt}
                  disabled={mode === "view" || isFetching}
                  placeholder={PollingScreenStringLabels.questionPromptPlaceholder}
                  onChange={(event) =>
                    updateQuestion(question.id, { prompt: event.target.value })
                  }
                />

                <label className="required-toggle polling-checkbox-field">
                  <input
                    type="checkbox"
                    className="polling-checkbox"
                    checked={question.required}
                    disabled={mode === "view" || isFetching}
                    onChange={(event) =>
                      updateQuestion(question.id, { required: event.target.checked })
                    }
                  />
                  {PollingScreenStringLabels.requiredLabel}
                </label>

                {question.type !== "boolean" ? (
                  <div>
                    <label>{PollingScreenStringLabels.optionsLabel}</label>
                    {question.options.map((option, optionIndex) => (
                      <div className="option-row" key={`${question.id}_${optionIndex}`}>
                        <input
                          className="polling-input"
                          value={option}
                          disabled={mode === "view" || isFetching}
                          onChange={(event) =>
                            updateOption(question.id, optionIndex, event.target.value)
                          }
                        />
                        {mode === "edit" ? (
                          <button
                            type="button"
                            className="polling-link-btn"
                            onClick={() => removeOption(question.id, optionIndex)}
                            disabled={
                              question.options.length <= 2 || isFetching
                            }
                          >
                            {PollingScreenStringLabels.remove}
                          </button>
                        ) : null}
                      </div>
                    ))}
                    {mode === "edit" ? (
                      <button
                        type="button"
                        className="polling-link-btn"
                        onClick={() => addOption(question.id)}
                        disabled={isFetching}
                      >
                        {PollingScreenStringLabels.addOption}
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <p className="polling-muted">
                    {PollingScreenStringLabels.booleanOptionsFixed}
                  </p>
                )}
              </div>
            ))}

            {mode === "edit" ? (
              <div className="add-question-row">
                {QUESTION_TYPES.map((type) => (
                  <button
                    type="button"
                    key={type.value}
                    className="polling-secondary-btn"
                    onClick={() => addQuestion(type.value)}
                    disabled={isFetching}
                  >
                    + {type.label}
                  </button>
                ))}
              </div>
            ) : null}

            {isNewDraft ? (
              <p className="polling-muted polling-draft-note">
                {PollingScreenStringLabels.newDraftNote}
              </p>
            ) : null}
          </div>

          <div className="polling-right-stack">
            <div className="polling-panel polling-preview-panel">
              <h3>{PollingScreenStringLabels.previewTitle}</h3>
              <p className="polling-muted">
                {PollingScreenStringLabels.previewDescription}
              </p>
              <div className="preview-area">
                <h4>{poll.name || PollingScreenStringLabels.untitledPoll}</h4>
                {poll.questions.map((question, index) => (
                  <div className="preview-question" key={`preview_${question.id}`}>
                    <div className="preview-question-title">
                      {index + 1}. {question.prompt || PollingScreenStringLabels.untitledQuestion}
                      {question.required ? " *" : ""}
                    </div>
                    {question.type === "boolean" ? (
                      <div className="preview-option-list">
                        <div className="preview-option-row">
                          <span className="preview-option-num">1.</span>
                          <span>{PollingScreenStringLabels.booleanYes}</span>
                        </div>
                        <div className="preview-option-row">
                          <span className="preview-option-num">2.</span>
                          <span>{PollingScreenStringLabels.booleanNo}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="preview-option-list">
                        {question.options.map((option, optionIndex) => (
                          <div
                            className="preview-option-row"
                            key={`${question.id}_preview_${optionIndex}`}
                          >
                            <span className="preview-option-num">
                              {optionIndex + 1}.
                            </span>
                            <span>{option || PollingScreenStringLabels.untitledOption}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {lastSavedAt ? (
                <div className="poll-save-note">
                  {PollingScreenStringLabels.lastSavedPrefix}{" "}
                  {new Date(lastSavedAt).toLocaleString()}
                </div>
              ) : null}
            </div>

            <div className="polling-panel polling-results-panel">
              <h3>{PollingScreenStringLabels.resultsTitle}</h3>
              {pollBaselineStatus && pollBaselineStatus !== "draft" ? (
                <span
                  className={`polling-status-chip polling-status-chip--${pollBaselineStatus}`}
                >
                  {getPollingStatusLabel(pollBaselineStatus)}
                </span>
              ) : null}
              <PollResultsBlock
                pollStatus={pollBaselineStatus || "draft"}
                summary={resultsSummary}
                loading={resultsStatus === "loading"}
                error={resultsError}
                socketStatus={resultsSocketStatus}
              />
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        show={showDeleteConfirm}
        buttonName={PollingScreenStringLabels.deletePoll}
        onClickHandle={(confirmed) => {
          if (confirmed) {
            handleDeletePoll();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        title={PollingScreenStringLabels.deletePollConfirmTitle}
        subTitle={formatDeletePollConfirmMessage(poll.name)}
      />
    </div>
  );
}

export default VisitPollingScreen;
